import * as THREE from 'three'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { moveObject3DChildToIndex } from '../../utils/scene-object/addObject3DToParent'

/**
 * 将 `.puppy` display.glb 中的 occurrence 级单槽 InstancedMesh 物化为业务 Mesh。
 *
 * display.glb 使用 `EXT_mesh_gpu_instancing` 保持离线文件紧凑；加载后的业务装配、拓扑和
 * 再实例化流程仍以每个 occurrence 独立 Mesh 为真相。该类只接受导出协议生成的单槽实例，
 * 并把 `object.matrix × instanceMatrix[0]` 合并到业务 Mesh 局部矩阵。
 */
export class PuppyDisplayInstancedMeshMaterializer {
  /**
   * 物化指定显示树中的全部单槽实例对象。
   *
   * @param root GLTFLoader 解析得到的 display 根。
   * @returns 实际替换的实例对象数量。
   */
  public materialize(root: THREE.Object3D): number {
    const instancedMeshes: THREE.InstancedMesh[] = []
    root.traverse((object) => {
      if (object instanceof THREE.InstancedMesh) {
        instancedMeshes.push(object)
      }
    })

    instancedMeshes.forEach((source) => {
      this.materializeSingleOccurrence(source)
    })
    return instancedMeshes.length
  }

  /**
   * 将一个单槽 InstancedMesh 替换成等价业务 Mesh。
   *
   * @param source GLTFLoader 从 `EXT_mesh_gpu_instancing` 恢复的实例对象。
   */
  private materializeSingleOccurrence(source: THREE.InstancedMesh): void {
    if (source.count !== 1) {
      throw new Error(`PuppyCAD display.glb 仅允许 occurrence 级单槽 InstancedMesh：${source.name}/${source.count}`)
    }
    if (source.children.length > 0) {
      throw new Error(`PuppyCAD display.glb 单槽 InstancedMesh 不允许携带子节点：${source.name || source.uuid}`)
    }
    const parent = source.parent
    if (!parent) {
      throw new Error(`PuppyCAD display.glb InstancedMesh 缺少父级：${source.name || source.uuid}`)
    }

    if (source.matrixAutoUpdate) {
      source.updateMatrix()
    }
    const instanceMatrix = new THREE.Matrix4()
    const localMatrix = new THREE.Matrix4()
    source.getMatrixAt(0, instanceMatrix)
    localMatrix.multiplyMatrices(source.matrix, instanceMatrix)

    const mesh = new THREE.Mesh(source.geometry, source.material)
    mesh.name = source.name
    mesh.visible = source.visible
    mesh.castShadow = source.castShadow
    mesh.receiveShadow = source.receiveShadow
    mesh.renderOrder = source.renderOrder
    mesh.layers.mask = source.layers.mask
    mesh.frustumCulled = source.frustumCulled
    mesh.userData = { ...source.userData }
    ImportedModelObjectMetadataService.copyObjectMetadata(source, mesh)
    localMatrix.decompose(mesh.position, mesh.quaternion, mesh.scale)
    mesh.updateMatrix()

    const sourceIndex = parent.children.indexOf(source)
    parent.add(mesh)
    moveObject3DChildToIndex(parent, mesh, Math.max(0, sourceIndex))
    source.removeFromParent()
    source.dispose()
  }
}
